import { useAuth } from "@/hooks/useAuth";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  User, 
  MapPin, 
  Camera, 
  Shield, 
  CheckCircle2, 
  AlertCircle,
  Sparkles,
  ArrowRight,
  Clock
} from "lucide-react";

const REMINDER_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds
const STORAGE_KEY = "profile-modal-last-shown";

export function ForceProfileModal() {
  const { tenant, user } = useAuth();
  const { completionPercentage, incompleteFields, isPublishable } = useProfileCompletion(tenant);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Check if modal should show based on time elapsed
  const shouldShowModal = useCallback(() => {
    if (!user?.id || isPublishable) return false;
    
    const lastShown = localStorage.getItem(`${STORAGE_KEY}-${user.id}`);
    if (!lastShown) return true;
    
    const elapsed = Date.now() - parseInt(lastShown, 10);
    return elapsed >= REMINDER_INTERVAL;
  }, [user?.id, isPublishable]);

  // Initial check and interval setup
  useEffect(() => {
    if (!user?.id) return;

    // Check immediately on mount
    if (shouldShowModal()) {
      setIsOpen(true);
      localStorage.setItem(`${STORAGE_KEY}-${user.id}`, Date.now().toString());
    }

    // Set up interval to check every minute
    const intervalId = setInterval(() => {
      if (shouldShowModal()) {
        setIsOpen(true);
        localStorage.setItem(`${STORAGE_KEY}-${user.id}`, Date.now().toString());
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(intervalId);
  }, [user?.id, shouldShowModal]);

  // Don't render if profile is complete
  if (!tenant || isPublishable) {
    return null;
  }

  const handleCompleteProfile = () => {
    setIsOpen(false);
    navigate("/settings");
  };

  const handleRemindLater = () => {
    if (user?.id) {
      localStorage.setItem(`${STORAGE_KEY}-${user.id}`, Date.now().toString());
    }
    setIsOpen(false);
  };

  // Group incomplete fields by category
  const groupedFields = incompleteFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof incompleteFields>);

  const categoryIcons: Record<string, React.ReactNode> = {
    basic: <User className="h-4 w-4" />,
    address: <MapPin className="h-4 w-4" />,
    media: <Camera className="h-4 w-4" />,
    location: <MapPin className="h-4 w-4" />,
    verification: <Shield className="h-4 w-4" />,
  };

  const categoryLabels: Record<string, string> = {
    basic: "Basic Info",
    address: "Address",
    media: "Photos",
    location: "Location",
    verification: "Verification",
  };

  const getProgressColor = () => {
    if (completionPercentage >= 80) return "bg-green-500";
    if (completionPercentage >= 50) return "bg-yellow-500";
    return "bg-orange-500";
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md overflow-hidden p-0 border-0">
        {/* Gradient Header */}
        <div className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-6 pb-12 text-primary-foreground">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-yellow-300 animate-pulse" />
              <span className="text-sm font-medium text-primary-foreground/80">Almost there!</span>
            </div>
            
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="text-2xl font-bold text-primary-foreground">
                Complete Your Profile
              </DialogTitle>
              <DialogDescription className="text-primary-foreground/80 text-base">
                Unlock all features and get listed on the VLE directory
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {/* Progress Circle - Overlapping */}
        <div className="relative -mt-8 flex justify-center">
          <div className="bg-background rounded-full p-2 shadow-lg">
            <div className="relative w-20 h-20 flex items-center justify-center">
              <svg className="w-20 h-20 -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-muted/30"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(completionPercentage / 100) * 226} 226`}
                  strokeLinecap="round"
                  className={`${getProgressColor()} transition-all duration-500`}
                  style={{ color: 'hsl(var(--primary))' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-foreground">{completionPercentage}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 pt-4 space-y-5">
          {/* Incomplete Fields */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              Fields to Complete
            </h4>
            
            <div className="grid gap-2">
              {Object.entries(groupedFields).map(([category, fields]) => (
                <div
                  key={category}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/50"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    {categoryIcons[category]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {categoryLabels[category]}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {fields.map(f => f.label).join(", ")}
                    </p>
                  </div>
                  <Badge variant="secondary" className="flex-shrink-0">
                    {fields.length}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-lg p-4 border border-green-200/50 dark:border-green-800/50">
            <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Benefits of 100% Profile
            </h4>
            <ul className="text-xs text-green-600 dark:text-green-400/80 space-y-1.5">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Get listed on public VLE directory
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Access all premium features
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Build customer trust with verified badge
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button 
              onClick={handleCompleteProfile}
              className="w-full h-12 text-base font-semibold gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25"
            >
              Complete Profile Now
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={handleRemindLater}
              className="w-full text-muted-foreground hover:text-foreground gap-2"
            >
              <Clock className="h-4 w-4" />
              Remind me in 30 minutes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
